/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CashFlowForecastDetailsDto, CashFlowForecastJobDto } from '@gh/builder-shared-data';
import {
  composeSetters,
  createArrayHelper,
  DateTimeTypes,
  EntityConfig,
  entityValidationSetter,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  fieldValidatorByEntityConfig,
  setDirty,
  setEntityField,
} from '@gh/common-shared';
import { AutocompleteItem, WithEntityState } from '@gh/common-shared-data';
import { FocusedCell } from '@gh/core-ui-grid';
import { isDateInvalid, isNotNil } from '@gh/core-util';
import { keyBy, omit } from 'lodash';

export type CashFlowForecastJob = WithEntityState<CashFlowForecastJobDto>;

export type CashFlowForecastDetails = {
  useJobSchedules: boolean;
  useVirtualSchedules: boolean;
  jobs: CashFlowForecastJob[];
  templates: AutocompleteItem[];
  templatesById: Hash<AutocompleteItem>;
  focusedCell?: FocusedCell;
};

export const CASH_FLOW_FORECAST_DETAILS_INIT: CashFlowForecastDetails = {
  useJobSchedules: true,
  useVirtualSchedules: false,
  jobs: [],
  templates: [],
  templatesById: {},
};

export const CASH_FLOW_FORECAST_JOB_ENTITY_CONFIG: EntityConfig<CashFlowForecastJob> = {
  startDate: {
    required: ({ templateId }) => isNotNil(templateId),
    type: DateTimeTypes.ShortDate,
  },
  templateId: {
    required: ({ startDate }) => isNotNil(startDate),
  },
};

export const getCashFlowForecastJobId = (job: CashFlowForecastJob) => job.jobId;
export const cashFlowForecastArr = createArrayHelper(getCashFlowForecastJobId);

export const cashFlowForecastJobFieldState = fieldStateByEntityConfig(CASH_FLOW_FORECAST_JOB_ENTITY_CONFIG);
export const validateCashFlowForecastJobField = fieldValidatorByEntityConfig(CASH_FLOW_FORECAST_JOB_ENTITY_CONFIG);
export const validateCashFlowForecastJob = entityValidatorByEntityConfig(CASH_FLOW_FORECAST_JOB_ENTITY_CONFIG);
export const setCashFlowForecastJobField = composeSetters(
  (entity: CashFlowForecastJob, field: keyof CashFlowForecastJob, value) => {
    if (field === 'startDate' && value && isDateInvalid(value)) {
      return setEntityField(entity, field, void 0);
    } else {
      return setEntityField(entity, field, value);
    }
  },
  setDirty,
  entityValidationSetter(validateCashFlowForecastJob));

export const cashFlowForecastDetailsFromDto = (dto: CashFlowForecastDetailsDto): CashFlowForecastDetails => {
  const { jobs, templates } = dto;
  return {
    useJobSchedules: true,
    useVirtualSchedules: false,
    jobs,
    templates,
    templatesById: keyBy(templates, 'id'),
  };
};

export const cashFlowForecastDetailsToDto = (dto: CashFlowForecastDetails): CashFlowForecastDetailsDto => {
  return {
    jobs: dto.jobs.map((job) => <CashFlowForecastJobDto>omit(job, 'entityState')),
    templates: [],
  };
};
